import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api } from '../../lib/api';
import { formatEuro } from '../../lib/format';
import { ChartEmpty } from './ChartEmpty';
import type { DashboardFiltersState } from './filters';
import { presetToBounds } from './filters';

interface Props {
  filters: DashboardFiltersState;
  limit?: number;
}

export function ChartTopClients({ filters, limit = 5 }: Props): JSX.Element {
  const bounds = presetToBounds(filters);
  const params = {
    period_from: bounds.period_from,
    period_to: bounds.period_to,
    user_id: filters.user_id,
    limit,
  };
  const query = useQuery({
    queryKey: ['dashboard', 'top-clients', params],
    queryFn: () => api.dashboard.topClients(params),
    staleTime: 60_000,
  });

  if (query.isLoading) {
    return (
      <Shell>
        <div className="h-[220px] bg-slate-800/40 rounded animate-pulse" />
      </Shell>
    );
  }
  if (query.isError || query.data === undefined) {
    return (
      <Shell>
        <ChartEmpty message="Erreur de chargement" />
      </Shell>
    );
  }
  if (query.data.rows.length === 0) {
    return (
      <Shell>
        <ChartEmpty />
      </Shell>
    );
  }
  // Recharts horizontal bar : layout="vertical" + axes inversés.
  const chartData = query.data.rows.map((r) => ({
    label: r.societe !== null && r.societe !== '' ? r.societe : r.nom,
    ca: r.ca_ht_centimes / 100,
    nb: r.nb_ventes,
    panier: r.panier_moyen_ht_centimes,
  }));
  // Hauteur dynamique : 44 px par barre + 40 px padding.
  const height = Math.max(180, chartData.length * 44 + 40);
  return (
    <Shell>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
          <XAxis
            type="number"
            tick={{ fill: '#94a3b8', fontSize: 11 }}
            stroke="#334155"
            tickFormatter={(v: number): string =>
              v >= 1000 ? `${(v / 1000).toFixed(0)} k€` : `${String(v)} €`
            }
          />
          <YAxis
            type="category"
            dataKey="label"
            tick={{ fill: '#cbd5e1', fontSize: 12 }}
            stroke="#334155"
            width={140}
          />
          <Tooltip
            contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8 }}
            labelStyle={{ color: '#e2e8f0' }}
            formatter={(
              value: number,
              _name: string,
              item: { payload?: { nb?: number; panier?: number } },
            ): [string, string] => {
              const nb = item.payload?.nb ?? 0;
              const panier = item.payload?.panier ?? 0;
              return [
                `${formatEuro(value * 100)} · ${String(nb)} ventes · panier moyen ${formatEuro(panier)}`,
                '',
              ];
            }}
          />
          <Bar dataKey="ca" fill="#1f7a3d" radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">Top 5 clients</div>
      {children}
    </div>
  );
}
